import { z } from 'zod';
import { MAX_RESUME_BASE64 } from './resume.js';

/**
 * Validation shared by the client forms and the Express routes.
 *
 * One definition, two consumers: the browser validates with the same rules and
 * the same error copy the server enforces, so the two can never drift apart.
 */

/** Must match contact.form.serviceOptions in src/content/contact.ts. */
export const serviceValues = [
  'software',
  'mobile',
  'ai-data',
  'ai-automation',
  'qa-automation',
  'workflow',
  'other',
] as const;

export const contactSchema = z.object({
  name: z.string().trim().min(2, 'Please enter your name').max(100),
  email: z.string().trim().toLowerCase().email('Enter a valid email address'),
  company: z.string().trim().max(120).optional().or(z.literal('')),
  service: z.enum(serviceValues, { errorMap: () => ({ message: 'Please choose a service' }) }),
  message: z
    .string()
    .trim()
    .min(20, 'Tell us a bit more — at least 20 characters')
    .max(4000, 'Please keep it under 4000 characters'),
  /**
   * Honeypot. Real users never see this field, so anything in it means a bot.
   *
   * Deliberately NOT constrained to empty here: a validation error would name
   * the field and tell the bot exactly what tripped it. The routes check this
   * value themselves and return a plain success, so a bot gets no signal to
   * adapt to.
   */
  website: z.string().optional(),
});

export type ContactInput = z.infer<typeof contactSchema>;

export const careersSchema = z.object({
  resume: z.object({
    filename: z.string().max(200).regex(/\.pdf$/i, 'Please choose a PDF file.'),
    content: z.string().min(4).max(MAX_RESUME_BASE64, 'Upload a PDF no larger than 5 MB.')
      .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'The PDF could not be read. Please select it again.'),
  }, { required_error: 'Please upload your resume as a PDF.' }),
  name: z.string().trim().min(2, 'Please enter your name').max(100),
  email: z.string().trim().toLowerCase().email('Enter a valid email address'),
  role: z.string().trim().min(2, 'What kind of work are you looking for?').max(120),
  portfolio: z
    .string()
    .trim()
    .url('Enter a valid URL, including https://')
    .optional()
    .or(z.literal('')),
  note: z
    .string()
    .trim()
    .min(20, 'Tell us a bit about yourself — at least 20 characters')
    .max(4000, 'Please keep it under 4000 characters'),
  consent: z.literal(true, {
    errorMap: () => ({ message: 'Please confirm we can keep your details on file' }),
  }),
  /** Honeypot — see the note on contactSchema.website. */
  website: z.string().optional(),
});

export type CareersInput = z.infer<typeof careersSchema>;

/** Shape returned by both endpoints. */
export interface ApiOk {
  ok: true;
}
export interface ApiError {
  ok: false;
  message?: string;
  /** Field name -> first error message. */
  errors?: Record<string, string>;
}
export type ApiResponse = ApiOk | ApiError;
